import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useUserRoles } from '@anytag/auth';
import { Option, SelectOption } from '@anytag/shared-types';
import { SelectionBar, Button, Select } from '@anytag/shared-atoms';
import { useSelectItems, useToggleState } from '@anytag/shared-hooks';
import {
  MarketplacePost,
  MarketplaceDetail,
  MarketplaceCampaignStatus,
  MarketplaceCampaignPostStatus
} from '@anytag/graphql';
import { UpdateCostsFormValues } from '../../hooks';
import { useUpdateMarketplacePostsStatus } from '../hooks';

interface ActionBarProps extends ReturnType<typeof useSelectItems<MarketplacePost, keyof MarketplacePost>> {
  marketplace: MarketplaceDetail;
}

export const ActionBar = ({
  allItemIds,
  selectedItems,
  selectedItemIds,
  setSelectedItemIds,
  hasAllItemsSelected,
  toggleSelectAllItems,
  marketplace: { id: marketplaceId, status, isGiftingCampaign }
}: ActionBarProps) => {
  const { t } = useTranslation();
  const loadingState = useToggleState();
  const { isAdminStaff } = useUserRoles();
  const { handleSelectStatus, UpdateMarketplacePostStatusModal } = useUpdateMarketplacePostsStatus({ marketplaceId });
  const {
    formState: { isDirty, isSubmitting }
  } = useFormContext<UpdateCostsFormValues>();

  const numberSelectedIds = selectedItemIds.length;
  const options: SelectOption<MarketplaceCampaignPostStatus>[] = [
    {
      label: t<string>('Option.Approve post'),
      value: MarketplaceCampaignPostStatus.APPROVED,
      hidden: status !== MarketplaceCampaignStatus.FINISHED || !isAdminStaff
    },
    {
      hidden: !isGiftingCampaign,
      label: t<string>('Option.Shipped'),
      value: MarketplaceCampaignPostStatus.SHIPPED
    },
    {
      label: t<string>('Option.Request to modify'),
      value: MarketplaceCampaignPostStatus.WARNING
    },
    {
      label: t<string>('Option.Change to “Effective”'),
      value: MarketplaceCampaignPostStatus.WARNING_SOLVED
    },
    {
      hidden: !isAdminStaff,
      label: t<string>('Option.Mark as “Ineffective”'),
      value: MarketplaceCampaignPostStatus.INEFFECTIVE
    }
  ].filter((option) => !option.hidden) as Option<MarketplaceCampaignPostStatus>[];

  return numberSelectedIds > 0 || isDirty ? (
    <>
      <SelectionBar
        disabled={loadingState.status}
        selectAllProps={{ onClick: toggleSelectAllItems }}
        checkboxProps={
          allItemIds.length
            ? {
                disabled: loadingState.status,
                onClick: toggleSelectAllItems,
                indeterminate: !hasAllItemsSelected,
                label: t('Selected', { count: numberSelectedIds })
              }
            : undefined
        }
      >
        <Select
          options={options}
          clearable={false}
          className="ml-4 w-45"
          disabled={loadingState.status}
          onChange={(value) => handleSelectStatus(value, selectedItems)}
        />
        <Button
          type="submit"
          variant="solid"
          disabled={!isDirty}
          title={t('Button.Save')}
          className="ml-auto px-4"
          loading={{ status: isSubmitting, title: t('Button.Saving') }}
        />
      </SelectionBar>

      <UpdateMarketplacePostStatusModal onSuccess={() => setSelectedItemIds([])} />
    </>
  ) : null;
};
